import React, { useEffect, useMemo, useState } from "react";
import { Input, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { getAvailableCourses } from "../../../../services/school-dosification/school-dosification.service";
import { AvailableCourse } from "../../../../services/school-dosification/school-dosification.types";
import { useNotification } from "../../../../hooks/useNotification";

const { Text } = Typography;

interface CourseSelectorModalProps {
  open: boolean;
  /** Escuela destino: decide qué cursos ya están ocupados. */
  schoolId: number;
  /** Programa destino: fija la asignatura de todo el catálogo. */
  programId: number;
  /** Trimestre del slot destino. No confundir con el filtro de trimestre de
   * ORIGEN, que es estado local del modal. */
  slotTrimester: number;
  /** Semana destino: titula el modal y se excluye de los cursos ya ocupados. */
  week: string | null;
  /** El slot destino es la semana de diagnóstico. */
  isDiagnosticSlot: boolean;
  /**
   * SCRUM-1076: courses the target week already holds (except the one being
   * replaced). A week cannot hold the same sequence twice.
   */
  blockedCourseIds: number[];
  saving: boolean;
  onCancel: () => void;
  onSelect: (course: AvailableCourse) => void;
}

/**
 * SCRUM-804: catálogo del "Seleccionar curso".
 *
 * El catálogo entero se pide una vez por programa y se filtra en memoria: son
 * los cursos de una sola asignatura y así el buscador y los filtros responden
 * sin ir al backend en cada tecla.
 */
const CourseSelectorModal: React.FC<CourseSelectorModalProps> = ({
  open,
  schoolId,
  programId,
  slotTrimester,
  week,
  isDiagnosticSlot,
  blockedCourseIds,
  saving,
  onCancel,
  onSelect,
}) => {
  const [courses, setCourses] = useState<AvailableCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [gradeId, setGradeId] = useState<number | undefined>();
  const [trimester, setTrimester] = useState<number | undefined>();
  const [search, setSearch] = useState("");
  const { notify } = useNotification();

  const blockedInWeek = useMemo(
    () => new Set(blockedCourseIds),
    [blockedCourseIds]
  );
  const isInWeek = (course: AvailableCourse) =>
    blockedInWeek.has(course.course_id);

  // Same reasoning as SCRUM-817 below: the click explains why it is blocked.
  const warnAlreadyInWeek = (course: AvailableCourse) =>
    notify(
      "warning",
      "Ese curso ya está en esta semana",
      `«${course.title}» ya está en esta semana, y una semana no puede tener dos veces la misma secuencia. Elegí otro curso.`,
    );

  /**
   * SCRUM-817: bloquear en silencio deja al admin clickeando sin entender por
   * qué no pasa nada, así que el click explica el motivo y qué hacer.
   */
  const warnAlreadyAssigned = (course: AvailableCourse) => {
    const slot = course.assigned_slot;
    if (!slot) return;

    notify(
      "warning",
      "Ese curso ya está en otra semana",
      `«${course.title}» ya ocupa la semana ${slot.week} del trimestre ${slot.trimester}. Quitalo de esa semana primero, o elegí otro curso.`,
    );
  };

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoading(true);
    // El slot destino viaja en el pedido: el backend contesta qué cursos ya
    // ocupan OTRA semana de este programa, y así el modal no ofrece algo que la
    // escritura va a rechazar (SCRUM-817).
    getAvailableCourses(programId, {
      school_id: schoolId,
      ...(week === null
        ? {}
        : { slot_trimester: slotTrimester, slot_week: week }),
    })
      .then((data) => {
        if (active) setCourses(data);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, schoolId, programId, slotTrimester, week]);

  const grades = useMemo(() => {
    const byId = new Map<number, string>();
    courses.forEach((course) =>
      byId.set(course.source_program.grade.id, course.source_program.grade.grade)
    );
    return Array.from(byId.entries()).map(([id, grade]) => ({ id, grade }));
  }, [courses]);

  const visibleCourses = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return courses.filter((course) => {
      // La semana de diagnóstico sólo acepta diagnósticos, y un diagnóstico no
      // puede ir a una semana normal: el backend lo rechaza igual, pero
      // ofrecerlo y que falle al guardar sería una trampa.
      if (course.diagnostic !== isDiagnosticSlot) return false;
      if (gradeId !== undefined && course.source_program.grade.id !== gradeId) {
        return false;
      }
      if (trimester !== undefined && course.source_trimester !== trimester) {
        return false;
      }
      return needle === "" || course.title.toLowerCase().includes(needle);
    });
  }, [courses, gradeId, trimester, search, isDiagnosticSlot]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={780}
      title={week ? `Seleccionar curso para la semana ${week}` : "Seleccionar curso"}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space direction="vertical" size={2}>
          <Text type="secondary">
            {isDiagnosticSlot
              ? "Esta es la semana de diagnóstico: sólo se puede reemplazar por el diagnóstico de otro grado."
              : "Se pueden usar cursos de esta misma asignatura de cualquier grado y de cualquier trimestre."}
          </Text>
          <Text type="secondary">
            Un curso no puede ocupar dos semanas de la misma asignatura: las dos
            compartirían una sola calificación. Tampoco puede estar dos veces en
            la misma semana.
          </Text>
        </Space>

        <Space wrap>
          <Select
            allowClear
            placeholder="Grado de origen"
            style={{ minWidth: 180 }}
            value={gradeId}
            onChange={setGradeId}
            options={grades.map((grade) => ({
              value: grade.id,
              label: grade.grade,
            }))}
          />
          <Select
            allowClear
            placeholder="Trimestre de origen"
            style={{ minWidth: 180 }}
            value={trimester}
            onChange={setTrimester}
            options={[1, 2, 3].map((value) => ({
              value,
              label: `T${value}`,
            }))}
          />
          <Input.Search
            allowClear
            placeholder="Buscar por nombre"
            style={{ minWidth: 240 }}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Space>

        <Table
          rowKey={(course) => `${course.source_program.id}-${course.course_id}`}
          size="small"
          loading={loading}
          dataSource={visibleCourses}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          scroll={{ y: 320 }}
          onRow={(course) => ({
            style:
              isInWeek(course) || course.assigned_slot
                ? { cursor: "not-allowed", opacity: 0.55 }
                : { cursor: saving ? "progress" : "pointer" },
            onClick: () => {
              if (saving) return;
              if (isInWeek(course)) {
                warnAlreadyInWeek(course);
                return;
              }
              if (course.assigned_slot) {
                warnAlreadyAssigned(course);
                return;
              }
              onSelect(course);
            },
          })}
          columns={[
            {
              title: "Curso",
              render: (_, course) =>
                isInWeek(course) ? (
                  <Space size={6}>
                    <Text>{course.title}</Text>
                    <Tag color="blue">Ya está en esta semana</Tag>
                  </Space>
                ) : (
                  <Space direction="vertical" size={0}>
                    <Space size={6}>
                      <Text>{course.title}</Text>
                      {course.assigned_slot && (
                        <Tag color="orange">Ya asignado</Tag>
                      )}
                    </Space>
                    {course.assigned_slot && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Ya está en T{course.assigned_slot.trimester} · Semana{" "}
                        {course.assigned_slot.week}
                      </Text>
                    )}
                  </Space>
                ),
            },
            {
              title: "Origen",
              render: (_, course) => (
                <Space size={4}>
                  <Tag>{course.source_program.grade.grade}</Tag>
                  <Text type="secondary">
                    T{course.source_trimester} · Semana {course.source_week}
                  </Text>
                </Space>
              ),
            },
            {
              title: "Páginas",
              dataIndex: "pages",
              render: (pages: string | null) => pages || "—",
            },
          ]}
        />
      </Space>
    </Modal>
  );
};

export default CourseSelectorModal;
